import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap, ShieldCheck, ArrowDown } from "lucide-react";
import { WHATSAPP_DEFAULT_MESSAGE, IMAGES } from "@/config/siteConfig";
import { waLink, trackEvent } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { scrollToId } from "@/components/Header";

const LINES = [
  { text: "Des services mobiles", accent: false },
  { text: "simples, avantageux", accent: true },
  { text: "et adaptés à vos besoins", accent: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="accueil" ref={ref} className="relative overflow-hidden bg-white pt-28 md:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 md:px-8 md:pb-28 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-gray-600"
          >
            <span className="h-2 w-2 rounded-full bg-jeem-yellow" />
            Mobile • Internet • Téléphonie • TV
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-black sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.accent ? (
                    <span className="relative inline-block">
                      <span className="relative z-10">{line.text}</span>
                      <motion.span
                        className="absolute inset-x-0 bottom-1 -z-0 h-4 bg-jeem-yellow md:h-5"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.6, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                        style={{ transformOrigin: "left" }}
                      />
                    </span>
                  ) : (
                    line.text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="mt-6 max-w-xl text-base text-gray-600 md:text-lg"
            data-testid="hero-subtitle"
          >
            Découvrez nos offres de télécommunications et obtenez rapidement les informations dont vous avez besoin avec l'aide de notre équipe.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <a
              href={waLink(WHATSAPP_DEFAULT_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("clic_whatsapp", { source: "hero" })}
              data-testid="hero-whatsapp-cta"
              className="flex h-14 items-center justify-center gap-2.5 rounded-full bg-jeem-yellow px-8 text-base font-bold text-black shadow-[0_10px_30px_-10px_rgba(255,222,0,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-jeem-yellow-dark"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Parler à un conseiller sur WhatsApp
            </a>
            <button
              type="button"
              onClick={() => { trackEvent("clic_voir_offres", { source: "hero" }); scrollToId("offres"); }}
              data-testid="hero-offres-cta"
              className="flex h-14 items-center justify-center gap-2 rounded-full border-2 border-black px-8 text-base font-semibold text-black transition-colors duration-300 hover:bg-black hover:text-white"
            >
              Voir les offres
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-gray-500"
            data-testid="hero-trust-line"
          >
            <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-black" /> Réponse rapide</span>
            <span className="hidden h-1 w-1 rounded-full bg-gray-300 sm:block" />
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-black" /> Service personnalisé</span>
            <span className="hidden h-1 w-1 rounded-full bg-gray-300 sm:block" />
            <span className="flex items-center gap-1.5"><WhatsAppIcon className="h-4 w-4 text-black" /> Assistance par WhatsApp</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
        >
          <motion.div
            style={{ y: bgY }}
            className="absolute -right-8 -top-8 h-64 w-64 rounded-[3rem] bg-jeem-yellow/80 md:h-80 md:w-80"
            aria-hidden="true"
          />
          <motion.div style={{ y: imgY }} className="relative">
            <img
              src={IMAGES.hero}
              alt={IMAGES.heroAlt}
              className="relative z-10 aspect-[4/5] w-full rounded-[2rem] border border-black/5 object-cover shadow-2xl"
              loading="eager"
              data-testid="hero-image"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.15 }}
              className="absolute -bottom-6 -left-4 z-20 rounded-2xl border border-black/5 bg-white px-5 py-4 shadow-xl md:-left-10"
              data-testid="hero-floating-badge"
            >
              <p className="font-display text-sm font-bold text-black">Sans contrat</p>
              <p className="text-xs text-gray-500">Rabais à vie sur certains forfaits</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
